<script lang="ts">
    import Image from "@lernetz/svelte-image/components/Image.svelte";
    import { requests, setupSharedProps } from "../../setup";
    import { t } from "@lernetz/svelte-translate";
    import { createEventDispatcher } from "svelte";
    import { Icon } from "@lernetz/svelte-icon";
    import { router } from "@inertiajs/svelte";
    import { repeatOnHold } from "../repeatOnHold";

    export let question;
    export let questionNr;
    export let mode = "single";
    export let last = false;
    export let dashboard;
    export let type;
    let showSolution:boolean = false;
    const params = new URLSearchParams(window.location.search);
    const pupils = Number(params.get('pupils') ?? 20);
    let preview = params.get('preview') ?? "false";
    let pupilsA = Math.ceil(pupils / 2);
    let pupilsB = Math.floor(pupils / 2);
    let selectedAnswer = 0;
    let lockAnswers = false;
    let showExplanation = true;

    const dispatch = createEventDispatcher();

    setupSharedProps();

    function nextQuestion() {
        dispatch("next");
    }

    function minusA() {
        pupilsA = pupilsA - 1;
        pupilsB = pupilsB + 1;
    }
    function plusA() {
        pupilsA = pupilsA + 1;
        pupilsB = pupilsB - 1;
    }

    function selectAnswer(answer: number) {
        if(lockAnswers == false && mode == 'single') {
            selectedAnswer = answer;
            lockAnswers = true;
            saveData();
        }
    }

    function saveData() {
        showSolution = true;
        if(preview == "false") {
            let data = {dashboardId: dashboard.id, questionId: question.id, type: type, mode: mode, right: 0, wrong: 0};
            if(mode == "class") {
                if(question.answer1correct) {
                    data.right = pupilsA;
                    data.wrong = pupilsB;
                }
                else {
                    data.right = pupilsB;
                    data.wrong = pupilsA;
                }
            }
            else {
                const correct =
                selectedAnswer === 1
                    ? question.answer1correct
                    : question.answer2correct;
                if(correct) {
                    data.right = 1;
                }
                else {
                    data.wrong = 1;
                }
            }
            dispatch("updateResults", {right: data.right, wrong: data.wrong});
            router.post('/saveData', data)
        }
    }

    $: choiceClasses = [0, 1].map(i => {
    const correct = i === 0
        ? question.answer1correct
        : question.answer2correct;

    // CLASS MODE
    if (mode === "class") {
        if (!showSolution) {
            return {
                border: "border-yellow-main",
                circle: "bg-yellow-accent",
                showIcon: false
            };
        }

        return {
            border: correct ? "border-correct-light" : "border-false-light",
            circle: correct ? "bg-correct-dark" : "bg-false-dark",
            showIcon: true
        };
    }

    // SINGLE MODE

    if (!showSolution) {
        return {
            border: "border-yellow-main",
            circle: "bg-yellow-accent",
            showIcon: false
        };
    }

    const selectedCorrect =
        selectedAnswer === 1
            ? question.answer1correct
            : question.answer2correct;

    // angeklickt & richtig
    if (selectedAnswer === i + 1 && correct) {
        return {
            border: "border-correct-light",
            circle: "bg-correct-dark",
            showIcon: true
        };
    }

    // angeklickt & falsch
    if (selectedAnswer === i + 1 && !correct) {
        return {
            border: "border-false-light",
            circle: "bg-false-dark",
            showIcon: true
        };
    }

    // nicht angeklickt & richtige Antwort (wenn falsch gewählt wurde)
    if (selectedAnswer !== i + 1 && correct && !selectedCorrect) {
        return {
            border: "border-correct-light",
            circle: "bg-correct-dark",
            showIcon: true
        };
    }

    // neutral
    return {
        border: "border-yellow-main",
        circle: "bg-yellow-accent",
        showIcon: false
    };
});

</script>
<div class="flex flex-col xl:flex-row gap-16 md:gap-32 xl:items-center xl:justify-center px-16 md:px-24 w-full max-w-[900px] mx-auto">
    <!-- Answer A: below question on mobile, left on desktop -->
    <div class="order-2 xl:order-1">
        <div class="flex items-center gap-12 xl:flex-col xl:items-center xl:gap-0">
            <div class={`w-40 h-40 shrink-0 text-center place-content-center rounded-full text-white text-28 relative z-20 ${choiceClasses[0].circle}`}>
                {#if choiceClasses[0].showIcon}
                    <Icon
                        name={choiceClasses[0].circle.includes("false") ? "close" : "check"}
                        class="w-28 h-28 place-self-center text-white fill-white"
                    />
                {:else}
                    A
                {/if}
            </div>
            <button
                class={`border-8 text-28 p-20 rounded-3xl text-center w-full xl:w-277 font-bold relative xl:-mt-20
                ${selectedAnswer == 1 ? 'bg-blue-main text-white' : 'bg-white text-black'}
                ${choiceClasses[0].border}`}
                on:click={() => selectAnswer(1)}
            >
                {question.answer1}
            </button>
        </div>
        {#if mode == 'class'}
        <div class="flex items-center justify-center gap-8 sm:gap-16 mt-24 sm:mt-48">
            <button use:repeatOnHold={minusA} disabled={showSolution || pupilsA <= 0} class="disabled:opacity-30 touch-none select-none p-8"><Icon name="minus" class="w-28 h-28 place-self-center text-blue-main"></Icon></button>
            <input type="number" disabled={true} bind:value={pupilsA} class="border-blue-light focus:border-blue-main border-2 rounded-3xl w-64 sm:w-128 h-56 sm:h-80 text-28 sm:text-36 font-bold text-center appearance-none
     [&::-webkit-outer-spin-button]:appearance-none
     [&::-webkit-inner-spin-button]:appearance-none
     [&::-webkit-outer-spin-button]:m-0
     [&::-webkit-inner-spin-button]:m-0"/>
            <button use:repeatOnHold={plusA} disabled={showSolution || pupilsB <= 0} class="disabled:opacity-30 touch-none select-none p-8"><Icon name="plus" class="w-28 h-28 place-self-center text-blue-main"></Icon></button>
        </div>
        {/if}
    </div>
    <!-- Question box: top on mobile, center on desktop -->
    <div class="order-1 xl:order-2 w-full xl:w-524 xl:shrink-0">
        <div>
            <div class="text-18 font-bold bg-line-subtle rounded-2xl px-10 text-grey-1 w-fit py-4 place-self-center relative z-20">{$t('question')} {questionNr + 1} / 10</div>
            <div class="font-bold text-28 bg-white py-32 text-blue-main rounded-3xl -mt-20 relative z-10 px-16">
                <div class="text-center">{question.question}</div>
                {#if showSolution}
                    {#if showExplanation}
                        <div class="mt-24 bg-yellow-main rounded-2xl px-16 pb-24 pt-16 text-black text-22 flex flex-col">
                            <div class="place-self-center">
                                <Icon name="bulb" class="w-28 h-28 place-self-center"></Icon>
                            </div>
                            <div class="text-center">{question.explanation}</div>
                            <button class="bg-yellow-accent w-32 h-32 self-end mt-24 rounded-lg" on:click={() => showExplanation = false}><Icon name="arrow_refresh" class="w-28 h-28 place-self-center"></Icon></button>
                        </div>
                    {:else}
                        <div class="flex flex-col">
                            <Image class="rounded-3xl w-full aspect-square mt-24 object-contain" request={requests.assets} vars={{ file_name: question.image?.file_name, preset: "medium.png" }} />
                            <button class="bg-yellow-accent w-32 h-32 self-end rounded-lg mt-24" on:click={() => showExplanation = true}><Icon name="arrow_refresh" class="w-28 h-28 place-self-center"></Icon></button>
                        </div>
                    {/if}
                {:else}
                    <Image class="rounded-3xl w-full aspect-square mt-24 object-contain" request={requests.assets} vars={{ file_name: question.image?.file_name, preset: "medium.png" }} />
                {/if}
            </div>
        </div>
    </div>
    <!-- Answer B: below A on mobile, right on desktop -->
    <div class="order-3">
        <div class="flex items-center gap-12 xl:flex-col xl:items-center xl:gap-0">
            <div class={`w-40 h-40 shrink-0 text-center place-content-center rounded-full text-white text-28 relative z-20 ${choiceClasses[1].circle}`}>
                {#if choiceClasses[1].showIcon}
                    <Icon
                        name={choiceClasses[1].circle.includes("false") ? "close" : "check"}
                        class="w-28 h-28 place-self-center text-white fill-white"
                    />
                {:else}
                    B
                {/if}
            </div>
            <button
                class={`border-8 text-28 p-20 rounded-3xl text-center w-full xl:w-277 font-bold relative xl:-mt-20
                ${selectedAnswer == 2 ? 'bg-blue-main text-white' : 'bg-white text-black'}
                ${choiceClasses[1].border}`}
                on:click={() => selectAnswer(2)}
            >
                {question.answer2}
            </button>
        </div>
        {#if mode == 'class'}
        <div class="flex items-center justify-center gap-8 sm:gap-16 mt-24 sm:mt-48">
            <button use:repeatOnHold={plusA} disabled={showSolution || pupilsB <= 0} class="disabled:opacity-30 touch-none select-none p-8"><Icon name="minus" class="w-28 h-28 place-self-center text-blue-main"></Icon></button>
            <input type="number" disabled={true} bind:value={pupilsB} class="border-blue-light focus:border-blue-main border-2 rounded-3xl w-64 sm:w-128 h-56 sm:h-80 text-28 sm:text-36 font-bold text-center appearance-none
     [&::-webkit-outer-spin-button]:appearance-none
     [&::-webkit-inner-spin-button]:appearance-none
     [&::-webkit-outer-spin-button]:m-0
     [&::-webkit-inner-spin-button]:m-0"/>
            <button use:repeatOnHold={minusA} disabled={showSolution || pupilsA <= 0} class="disabled:opacity-30 touch-none select-none p-8"><Icon name="plus" class="w-28 h-28 place-self-center text-blue-main"></Icon></button>
        </div>
        {/if}
    </div>
</div>
<div class="mt-32 md:mt-64 xl:mt-112 px-16 md:px-24">
    {#if mode == 'class'}
        <button class="bg-blue-main text-white text-18 font-bold py-12 px-24 rounded-lg" class:hidden={showSolution} on:click={saveData}>{$t('show solution')}</button>
    {/if}
    <button class="bg-blue-main text-white text-18 font-bold py-12 px-24 rounded-lg flex gap-12" class:hidden={!showSolution} on:click={nextQuestion}>{$t('next')}
        <Icon name="arrow-right" class="w-28 h-28 place-self-center"></Icon>
    </button>
</div>
